import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/**
 * useCurrentUser — the caller's own identity envelope from GET /api/current-user.
 *
 * Shares the ['current-user'] query key (and 5-minute staleTime) with
 * useOrgTimezone and the app shell, so react-query dedupes the fetch — reading
 * the current user here costs no extra request. Used to pin a "Me" option atop
 * the calendar's consultant filter (and available to any surface that wants the
 * signed-in user's id/name without threading it through props).
 */
export interface CurrentUser {
  id: string;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  role?: string;
  timezone?: string | null;
  [key: string]: unknown;
}

export function useCurrentUser(): CurrentUser | undefined {
  const { data } = useQuery<CurrentUser>({
    queryKey: ['current-user'],
    queryFn: ({ signal }) => api.get<CurrentUser>('/api/current-user', signal),
    staleTime: 5 * 60 * 1000,
  });
  return data;
}

/** The signed-in user's display name: "First Last", falling back to email, then ''. */
export function currentUserName(u: CurrentUser | undefined): string {
  if (!u) return '';
  return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '';
}
